import React, { useState } from 'react';
import {Error, Warning, Info, Search, CheckCircle} from '@mui/icons-material';

const ErrorCodes = () => {
    const [searchTerm, setSearchTerm] = useState('');

    const errorCategories = [
        {
            category: 'Authentification',
            icon: Warning,
            errors: [
                { code: 'AUTH_001', message: 'Credentials invalides', description: 'Client ID ou Secret incorrect', solution: 'Vérifiez vos credentials' },
                { code: 'AUTH_002', message: 'Token expiré', description: 'Le token d\'accès a expiré', solution: 'Récupérez un nouveau token' },
                { code: 'AUTH_003', message: 'Scope insuffisant', description: 'Permissions insuffisantes', solution: 'Vérifiez les scopes de votre application' }
            ]
        },
        {
            category: 'Paiement',
            icon: Error,
            errors: [
                { code: 'PAY_001', message: 'Montant invalide', description: 'Le montant doit être positif', solution: 'Corrigez le montant' },
                { code: 'PAY_002', message: 'Devise non supportée', description: 'Devise non reconnue', solution: 'Utilisez USD ou CDF' },
                { code: 'PAY_003', message: 'Référence dupliquée', description: 'Référence déjà utilisée', solution: 'Utilisez une référence unique' }
            ]
        },
        {
            category: 'Validation',
            icon: Info,
            errors: [
                { code: 'VAL_001', message: 'Champ obligatoire manquant', description: 'Un champ requis est absent', solution: 'Vérifiez tous les champs obligatoires' },
                { code: 'VAL_002', message: 'Format invalide', description: 'Format de données incorrect', solution: 'Respectez le format attendu' },
                { code: 'VAL_003', message: 'URL de callback invalide', description: 'URL de redirection mal formée', solution: 'Utilisez une URL HTTPS valide' }
            ]
        },
        {
            category: 'Système',
            icon: Warning,
            errors: [
                { code: 'SYS_001', message: 'Service indisponible', description: 'Maintenance en cours', solution: 'Réessayez plus tard' },
                { code: 'SYS_002', message: 'Timeout', description: 'Délai dépassé', solution: 'Réessayez la requête' },
                { code: 'SYS_003', message: 'Limite de taux atteinte', description: 'Trop de requêtes', solution: 'Réduisez la fréquence' }
            ]
        }
    ];

    const filteredCategories = errorCategories.map(category => ({
        ...category,
        errors: category.errors.filter(error =>
            error.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
            error.message.toLowerCase().includes(searchTerm.toLowerCase()) ||
            error.description.toLowerCase().includes(searchTerm.toLowerCase())
        )
    })).filter(category => category.errors.length > 0);

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-slate-950 dark:text-slate-100 pt-16">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Hero Section */}
                <div className="text-center mb-12">
                    <div className="flex justify-center mb-6">
                        <div className="p-4 bg-red-100 rounded-2xl">
                            <Error className="text-4xl text-red-600" />
                        </div>
                    </div>
                    <h1 className="text-4xl font-bold text-gray-900 mb-4">Codes d'Erreur</h1>
                    <p className="text-xl text-gray-600">
                        Référence complète des codes d'erreur et leurs solutions
                    </p>
                </div>

                {/* Search Bar */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Rechercher un code d'erreur ou un message..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                        />
                    </div>
                </div>

                <div className="space-y-8">
                    {filteredCategories.map((category, categoryIndex) => {
                        const IconComponent = category.icon;
                        return (
                            <div key={categoryIndex} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                                <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 border-b border-gray-200">
                                    <div className="flex items-center space-x-3">
                                        <IconComponent className="text-2xl text-gray-600" />
                                        <h2 className="text-xl font-bold text-gray-900">{category.category}</h2>
                                        <span className="bg-gray-200 text-gray-700 px-2 py-1 rounded text-sm">
                      {category.errors.length} erreur{category.errors.length > 1 ? 's' : ''}
                    </span>
                                    </div>
                                </div>

                                <div className="divide-y divide-gray-200">
                                    {category.errors.map((error, errorIndex) => (
                                        <div key={errorIndex} className="p-6 hover:bg-gray-50 transition-colors">
                                            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                                                <div className="flex-1">
                                                    <div className="flex items-center space-x-3 mb-2">
                                                        <code className="bg-red-100 text-red-800 px-2 py-1 rounded text-sm font-mono">
                                                            {error.code}
                                                        </code>
                                                        <span className="font-semibold text-gray-900">{error.message}</span>
                                                    </div>
                                                    <p className="text-gray-600 mb-2">{error.description}</p>
                                                    <div className="flex items-center space-x-2 text-sm">
                                                        <span className="text-green-600 font-medium">Solution:</span>
                                                        <span className="text-gray-700">{error.solution}</span>
                                                    </div>
                                                </div>

                                                <div className="flex space-x-2">
                                                    <button className="px-3 py-1 bg-amber-100 text-amber-700 rounded text-sm hover:bg-amber-200 transition-colors">
                                                        Exemple
                                                    </button>
                                                    <button className="px-3 py-1 bg-blue-100 text-blue-700 rounded text-sm hover:bg-blue-200 transition-colors">
                                                        Documentation
                                                    </button>
                                                </div>
                                            </div>

                                            {/* Example Response */}
                                            <div className="mt-4 bg-gray-900 rounded-lg p-4">
                        <pre className="text-red-400 text-sm overflow-x-auto">
                          <code>
{`// Réponse d'erreur
{
  "error": true,
  "code": "${error.code}",
  "message": "${error.message}",
  "details": "${error.description}"
}`}
                          </code>
                        </pre>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Error Handling Guide */}
                <div className="mt-12 bg-amber-50 border border-amber-200 rounded-2xl p-8">
                    <h2 className="text-2xl font-bold text-amber-900 mb-4">Guide de Gestion des Erreurs</h2>

                    <div className="grid md:grid-cols-2 gap-8">
                        <div>
                            <h3 className="font-semibold text-amber-900 mb-3">Bonnes Pratiques</h3>
                            <ul className="space-y-2 text-amber-800">
                                <li className="flex items-center space-x-2">
                                    <CheckCircle className="text-green-500 text-sm" />
                                    <span>Implémentez une gestion d'erreurs centralisée</span>
                                </li>
                                <li className="flex items-center space-x-2">
                                    <CheckCircle className="text-green-500 text-sm" />
                                    <span>Logguez les erreurs pour le debugging</span>
                                </li>
                                <li className="flex items-center space-x-2">
                                    <CheckCircle className="text-green-500 text-sm" />
                                    <span>Affichez des messages utilisateur clairs</span>
                                </li>
                            </ul>
                        </div>

                        <div>
                            <h3 className="font-semibold text-amber-900 mb-3">À Éviter</h3>
                            <ul className="space-y-2 text-amber-800">
                                <li className="flex items-center space-x-2">
                                    <Error className="text-red-500 text-sm" />
                                    <span>Exposer les détails techniques aux utilisateurs</span>
                                </li>
                                <li className="flex items-center space-x-2">
                                    <Error className="text-red-500 text-sm" />
                                    <span>Ignorer les erreurs de validation</span>
                                </li>
                                <li className="flex items-center space-x-2">
                                    <Error className="text-red-500 text-sm" />
                                    <span>Réessayer immédiatement sans délai</span>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ErrorCodes;